import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { ArrowUpOutline, Flex, Text } from '@bonusly/design-system';

interface BizyFollowUpSuggestionsProps {
	/** Paste-ready imperative commands suggested by the agent. */
	suggestions: string[];
	/** Send the suggestion verbatim as the next message. */
	onSelect: (suggestion: string) => void;
	/** Disable while a turn is in flight (mirrors the composer's disabled state). */
	isDisabled?: boolean;
	fontSize?: 'sm' | 'md';
}

/**
 * Renders the Data Agent's follow-up suggestions as one-tap rows under a
 * "Follow-ups" heading. Each row is a button: a small send affordance on the
 * left and the full imperative command as the label. Clicking sends the
 * suggestion verbatim — no retyping.
 *
 * The text is the label (not itself a chip) so longer commands stay readable;
 * the whole row is the tap target, with the arrow as the visual affordance.
 * Rows disable while a previous turn is still processing, matching the
 * composer's send button.
 */
export function BizyFollowUpSuggestions({ suggestions, onSelect, isDisabled = false, fontSize = 'sm' }: BizyFollowUpSuggestionsProps) {
	const { t } = useTranslation('admin_bizy');
	const { track } = useAnalytics();
	const items = suggestions.filter((suggestion) => suggestion.trim().length > 0);
	if (items.length === 0) return null;

	const handleSelect = (suggestion: string, position: number) => {
		if (isDisabled) return;
		track(AnalyticsEvent.Bizy.FollowUpClicked, { position, path: window.location.pathname });
		onSelect(suggestion);
	};

	return (
		<Flex direction="column" gap={2} mb={4} data-testid="bizy-follow-ups">
			<Text fontSize="md" fontWeight="semibold" color="text.default" mb={0.5}>
				{t('followUps.heading', 'Follow-ups')}
			</Text>
			{items.map((suggestion, idx) => (
				<Flex
					key={idx}
					as="button"
					type="button"
					disabled={isDisabled}
					aria-label={suggestion}
					onClick={() => handleSelect(suggestion, idx)}
					align="center"
					gap={3}
					textAlign="left"
					borderWidth="1px"
					borderColor="border.soft"
					borderRadius={12}
					px={3}
					py={2.5}
					opacity={isDisabled ? 0.5 : 1}
					cursor={isDisabled ? 'not-allowed' : 'pointer'}
					transition="background 0.15s ease, border-color 0.15s ease"
					{...(!isDisabled && { _hover: { bg: 'secondary.bgAlpha', borderColor: 'border.default' } })}
				>
					<Flex align="center" justify="center" minW={7} h={7} flexShrink={0} borderRadius="full" bg="secondary.bg" color="secondary.fgOnSurface">
						<ArrowUpOutline boxSize={4} />
					</Flex>
					<Text fontSize={fontSize} color="text.default" lineHeight="short">
						{suggestion}
					</Text>
				</Flex>
			))}
		</Flex>
	);
}
